import { useState, useMemo, useEffect } from 'react'
import * as PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { formatInTimeZone, toDate } from 'date-fns-tz'
import { useNavigate, useParams } from 'react-router-dom'

import { useDebounce } from '@/hooks'
import { getApiErrorMessage } from '@/utils'

import Dashboard from '../../Dashboard'
import { selectUserData } from '../../../reducers/userDataReducer'
import { getClients, selectClients } from '../../../reducers/clientsReducer'
import {
  selectShiftManagers,
  fetchShiftManagers,
} from '../../../reducers/Organisation/shiftManagersReducer'
import API from '../../../api/api'
import { selectActiveBuilding } from '../../../reducers/buildingSelectorReducer'

import BodyContainer from './BodyContainer'
import SearchComponent from './SearchComponent'
import AddGuestForm from './AddGuestForm'
import ShiftNoteForm from './ShiftNoteForm'
import Navbar from './Navbar'

const ActionLog = ({
  getClients,
  users,
  fetchShiftManagers,
  staffPersons,
  currentUser,
  selectedBuilding,
}) => {
  const [isLoading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({
    searchFilter: '',
    dateRangeFilter: null,
    typeFilter: [],
    staffFilter: [],
    residentFilter: [],
    skip: 0,
  })
  const [errorMessage, setErrorMessage] = useState(null)
  const [shiftNoteFormVisible, setShiftNoteFormVisible] = useState(false)
  const [addGuestFormVisible, setAddGuestFormVisible] = useState(false)
  const [actionLogs, setActionLogs] = useState([])
  const [maxCountLogs, setMaxCountLogs] = useState(0)
  const [filteredActionLogs, setFilteredActionLogs] = useState(actionLogs)
  const [editedLog, setEditedLog] = useState(null)
  const residentsDebounce = useDebounce({ delay: 500 })

  const { buildingId: paramsBuildingId } = useParams()
  const navigate = useNavigate()

  const setParams = (buildingId) => {
    const rootRoute = `/dashboard/building-dashboard/action-log`
    const building = buildingId || paramsBuildingId
    if (!building) {
      navigate(rootRoute)
    } else {
      navigate(`${rootRoute}/${building}`)
    }
  }

  const fetchClients = async (buildingId) => {
    try {
      return await getClients(undefined, buildingId, undefined, undefined, 25)
    } catch (error) {
      console.error(error)
    }
  }

  const loadResidentOptions = (inputValue, callback) => {
    residentsDebounce.debounce(async () => {
      try {
        const options = await API.getClients(0, 25, selectedBuilding.id, 0, inputValue)
        const filteredOptions = options.map((client) => ({
          ...client,
          value: client.id,
          label: `${client.user.firstName} ${client.user.lastName} #${client.apartment}`,
        }))
        callback(filteredOptions)
      } catch (error) {
        console.error(error)
      }
    })
  }

  const getActionLogs = async (skip = 0) => {
    if (selectedBuilding) {
      const { searchFilter } = filters
      const thisStart = filters.dateRangeFilter ? filters.dateRangeFilter[0] : 0
      const thisEnd = filters.dateRangeFilter ? filters.dateRangeFilter[1] : 0

      setLoading(true)

      const startDate = thisStart
        ? toDate(
            formatInTimeZone(thisStart, selectedBuilding.timezone, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
          ).getTime()
        : 0

      const endDate = thisEnd
        ? toDate(
            formatInTimeZone(
              new Date(thisEnd).setHours(23, 59, 0, 0),
              selectedBuilding.timezone,
              "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
            ),
          ).getTime()
        : 0

      try {
        if (skip) {
          const { tasks } = await API.getActionLogs(
            startDate,
            endDate,
            selectedBuilding.id,
            searchFilter,
            skip,
          )
          setActionLogs((prevActionLogs) => [...prevActionLogs, ...tasks])
        } else {
          setFilters({ ...filters, skip: 0 })
          const { count, tasks } = await API.getActionLogs(
            startDate,
            endDate,
            selectedBuilding.id,
            searchFilter,
            0,
          )
          setMaxCountLogs(count)
          setActionLogs(tasks)
        }
      } catch (err) {
        setErrorMessage(getApiErrorMessage(err))
      } finally {
        setLoading(false)
      }
    }
  }

  const onEditLog = (logData) => {
    setEditedLog(logData)
    setShiftNoteFormVisible(true)
  }

  const onLoadLogs = () => {
    if (maxCountLogs > actionLogs.length) {
      setFilters({ ...filters, skip: filters.skip + 100 })
      getActionLogs(filters.skip + 100)
    }
  }

  const onCloseShiftNoteForm = () => {
    setEditedLog(null)
    setShiftNoteFormVisible(false)
  }

  const onCloseAddGuestForm = () => {
    setAddGuestFormVisible(false)
  }

  const memoizedLogs = useMemo(() => {
    return filteredActionLogs
      ? filteredActionLogs.sort((a, b) => new Date(b.date) - new Date(a.date))
      : []
  }, [filteredActionLogs])

  useEffect(() => {
    let isCancelled = false

    ;(async () => {
      if (selectedBuilding && selectedBuilding.id) {
        try {
          await fetchClients(selectedBuilding.id)
          await fetchShiftManagers(selectedBuilding.id)

          if (paramsBuildingId !== selectedBuilding.id && !isCancelled) {
            setSearch('')
            setParams(selectedBuilding.id)
          }
        } catch (error) {
          console.error(error)
        }
      }
    })()

    return () => {
      isCancelled = true
    }
  }, [selectedBuilding])

  useEffect(() => {
    getActionLogs()
  }, [selectedBuilding])

  useEffect(() => {
    let logs = actionLogs
    if (
      filters.staffFilter &&
      filters.staffFilter.length &&
      filters.staffFilter[0].value !== 'all'
    ) {
      logs = logs.filter((log) =>
        filters.staffFilter.map((staffData) => staffData.value).includes(log.shiftManagerId),
      )
    }
    if (filters.typeFilter && filters.typeFilter.length && filters.typeFilter[0].value !== 'all') {
      logs = logs.filter((log) =>
        filters.typeFilter.map((type) => type.value).includes(log.category),
      )
    }
    if (
      filters.residentFilter &&
      filters.residentFilter.length &&
      filters.residentFilter[0].value !== 'all'
    ) {
      logs = logs.filter((log) =>
        filters.residentFilter
          .map((residentData) => residentData.value)
          .includes(log.clientId ? log.clientId.id : null),
      )
    }
    setFilteredActionLogs(logs)
  }, [actionLogs])

  return (
    <Dashboard.Container className={`ctp-action-log`}>
      <Navbar
        errorMessage={errorMessage}
        setShiftNoteFormVisible={setShiftNoteFormVisible}
        setAddGuestFormVisible={setAddGuestFormVisible}
      />
      <div className="ctp-action-log-flex-container">
        <SearchComponent
          search={search}
          setFilters={setFilters}
          onSearchClick={() => getActionLogs()}
          users={users}
          staffPersons={staffPersons}
          skip={filters.skip}
          loadResidentOptions={loadResidentOptions}
        />
        <BodyContainer
          isLoading={isLoading}
          actionLogs={memoizedLogs}
          filters={filters}
          staffPersons={staffPersons}
          onEditLog={(logData) => onEditLog(logData)}
          loadMoreLogs={onLoadLogs}
          building={selectedBuilding}
        />
      </div>
      {shiftNoteFormVisible && (
        <ShiftNoteForm
          editedLog={editedLog || null}
          handleCancel={onCloseShiftNoteForm}
          residents={users}
          staffPersons={staffPersons}
          currentUser={currentUser}
          refreshDataActionLogs={() => getActionLogs()}
          building={selectedBuilding}
          loadResidentOptions={loadResidentOptions}
        />
      )}
      {addGuestFormVisible && (
        <AddGuestForm
          handleCancel={onCloseAddGuestForm}
          residents={users}
          refreshDataActionLogs={() => getActionLogs()}
          building={selectedBuilding}
          loadResidentOptions={loadResidentOptions}
        />
      )}
    </Dashboard.Container>
  )
}

ActionLog.propTypes = {
  buildings: PropTypes.array,
  users: PropTypes.array,
  getClients: PropTypes.func,
  fetchShiftManagers: PropTypes.func,
  staffPersons: PropTypes.array,
  currentUser: PropTypes.object,
  selectedBuilding: PropTypes.string,
}

const mapClientToUser = ({ id, apartment, user: { email, firstName, lastName }, phone }) => ({
  value: id,
  label: `${firstName} ${lastName} #${apartment}`,
  email,
  phone,
  apartment,
})

const mapStaffToUser = ({ id, user: { email, firstName, lastName, avatar } }) => ({
  value: id,
  label: `${firstName} ${lastName}`,
  email,
  avatar,
})

export default connect(
  (state) => {
    return {
      currentUser: selectUserData(state),
      users: selectClients(state).map(mapClientToUser),
      staffPersons: selectShiftManagers(state).map(mapStaffToUser),
      selectedBuilding: selectActiveBuilding(state),
    }
  },
  {
    getClients,
    fetchShiftManagers,
  },
)(ActionLog)
